import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-stat-tile>` says about itself.
 *
 * ── `intent` IS AN INPUT THAT IS NOT OBSERVED ────────────────────────────────
 * `observedAttributes` is label, value, delta, trend and clickable — the five the class reads in
 * script. `intent` is read by the STYLESHEET, through `:host([intent="…"])` selectors, so it needs
 * no callback and is a real input all the same. Reading the observed list alone would have missed
 * it, the way it would have missed SkeletonBlock's variant.
 *
 * ── WHY THE TINTED TILE SWAPS ITS INK ────────────────────────────────────────
 * On a semantic tint the label and value read the theme-FIXED intent ink tokens rather than the
 * ordinary text tokens, which flip with the theme. That is what keeps a tinted tile legible in both
 * themes, and it is why the token table below lists an ink token per intent as well as a fill: a
 * brand override that changes one without the other breaks the tile in exactly one theme.
 *
 * ── THE TILE DOES NOT DO ARITHMETIC ──────────────────────────────────────────
 * `value`, `delta` and `trend` are three separate strings the app decides. Nothing here computes a
 * delta, compares it to anything, or derives the direction — a tile reading "-8%" with trend="up"
 * will draw a rising arrow in the success ink and be wrong, and nothing in the component will stop
 * it.
 */
const docs: ComponentDocs = {
  aliases: ['stat', 'KPI', 'metric card', 'big number', 'scorecard', 'summary tile', 'counter'],

  answers: [
    'show one headline number somebody should notice',
    'show whether a number went up or down since last time',
    'flag a figure as good, watch-it, or bad',
    'let somebody click a headline number through to the records behind it',
  ],

  placements: ['homePage', 'appPage', 'recordPage'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What the number is — a short mono eyebrow above it. Not drawn at all when empty, which ' +
        'leaves a figure with nothing saying what it counts.',
    },
    {
      name: 'value',
      attribute: 'value',
      type: 'text',
      description:
        'The figure itself, already formatted — this piece rounds, groups and abbreviates nothing. ' +
        'Set as text rather than a number so a currency mark, a unit or a percent sign can travel ' +
        'with it. Drawn large, in tabular figures.',
    },
    {
      name: 'delta',
      attribute: 'delta',
      type: 'text',
      description:
        'The change since whatever the comparison is — "+12%", "-3", "+4 this week". The tile does ' +
        'not work it out, and does not say what it is compared against, so the string has to.',
    },
    {
      name: 'trend',
      attribute: 'trend',
      type: 'text',
      options: ['up', 'down', 'flat'],
      description:
        'Which way the delta went: up, down or flat. Draws the matching arrow and colours the delta ' +
        'in the semantic ink. Nothing is drawn unless there is a delta to draw it beside, and ' +
        'nothing checks that the direction agrees with the number.',
    },
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['info', 'success', 'warning', 'danger'],
      description:
        'Tint the WHOLE tile: info, success, warning or danger. It says something about the figure ' +
        'rather than about the change — a red tile is a bad number, which is not the same statement ' +
        'as a downward trend. Read by the stylesheet rather than by script.',
    },
    {
      name: 'clickable',
      attribute: 'clickable',
      type: 'boolean',
      description:
        'Make the tile a deep link to whatever is behind the number: a pointer, an accent border on ' +
        'hover and focus, a place in the tab order, and the caos-stat-select event. Without it the ' +
        'tile is inert and clicking it does nothing.',
      default: false,
    },
  ],

  events: [
    {
      name: 'caos-stat-select',
      when:
        'A clickable tile is activated — by click, Enter or Space. A tile without clickable set ' +
        'raises nothing, whatever is done to it.',
      carries:
        '{ label, value } — the two strings the tile was given. Note it does NOT carry an id, so the ' +
        'host works out what to open from the label it set.',
    },
  ],

  state: [],

  slots: [
    {
      name: 'visual',
      takes:
        'A small graphic at the right-hand end of the footer line, beside the delta — a sparkline or ' +
        'similar. The library has nothing to put here yet, so it takes an app-supplied element. The ' +
        'slot is not drawn at all when it is empty.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The ground of an untinted tile.' },
    { token: '--caos-color-border', controls: 'The border of an untinted tile.' },
    { token: '--caos-radius-lg', controls: 'The corners of the tile.' },
    { token: '--caos-space-4', controls: 'The padding inside the tile.' },
    { token: '--caos-space-2', controls: 'The gap between the label, the value and the footer line.' },
    { token: '--caos-space-1', controls: 'The gap between the trend arrow and the delta.' },
    { token: '--caos-color-accent', controls: 'The border a clickable tile takes on hover and focus.' },
    { token: '--caos-accent-wash', controls: 'The ring around a focused clickable tile.' },
    { token: '--caos-font-mono', controls: 'The face of the label.' },
    { token: '--caos-text-label', controls: 'The size of the label.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the label.' },
    { token: '--caos-tracking-wide', controls: 'The letter spacing of the label.' },
    { token: '--caos-color-text-muted', controls: 'The label ink, and the delta of a flat trend.' },
    { token: '--caos-font-display', controls: 'The face of the figure.' },
    { token: '--caos-text-kpi', controls: 'The size of the figure.' },
    { token: '--caos-color-text', controls: 'The ink of the figure on an untinted tile.' },
    { token: '--caos-font', controls: 'The face of the delta.' },
    { token: '--caos-color-info-bg', controls: 'The fill of an info tile.' },
    { token: '--caos-color-info', controls: 'The border of an info tile, mixed toward transparent.' },
    { token: '--caos-color-info-text', controls: 'The figure ink on an info tile.' },
    { token: '--caos-color-info-text-muted', controls: 'The label ink on an info tile.' },
    { token: '--caos-color-success-bg', controls: 'The fill of a success tile.' },
    { token: '--caos-color-success', controls: 'The border of a success tile, mixed toward transparent.' },
    { token: '--caos-color-success-text', controls: 'The figure ink on a success tile, and the delta of an upward trend.' },
    { token: '--caos-color-success-text-muted', controls: 'The label ink on a success tile.' },
    { token: '--caos-color-warning-bg', controls: 'The fill of a warning tile.' },
    { token: '--caos-color-warning', controls: 'The border of a warning tile, mixed toward transparent.' },
    { token: '--caos-color-warning-text', controls: 'The figure ink on a warning tile.' },
    { token: '--caos-color-warning-text-muted', controls: 'The label ink on a warning tile.' },
    { token: '--caos-color-danger-bg', controls: 'The fill of a danger tile.' },
    { token: '--caos-color-danger', controls: 'The border of a danger tile, mixed toward transparent.' },
    { token: '--caos-color-danger-text', controls: 'The figure ink on a danger tile, and the delta of a downward trend.' },
    { token: '--caos-color-danger-text-muted', controls: 'The label ink on a danger tile.' },
  ],

  parts: [
    { part: 'tile', is: 'the tile' },
    { part: 'label', is: 'the label above the figure' },
    { part: 'value', is: 'the figure' },
    { part: 'delta', is: 'the change beside the figure' },
    { part: 'visual', is: 'the area a sparkline or other visual is slotted into' },
  ],

  methods: [],

  placing:
    'A tile fills the width it is given and hugs its content downward, so a row of them is made by ' +
    'the region that holds them rather than by the tile — put them in a grid and they line up. They ' +
    'go at the TOP of a surface, above the list or the sections, which is the only place a headline ' +
    'figure is read before the detail it summarises.',

  whenToUse:
    'One number that somebody should see before they see anything else, with enough room for the ' +
    'label that says what it counts and, where there is one, the change since last time. Three to ' +
    'six across the head of a surface is the shape; twenty of them is a table.',

  insteadUse: [
    {
      name: 'AppHome',
      when:
        "You are building the whole landing surface — what needs attention, gathered — rather than " +
        'placing one figure on a page you already have.',
    },
    {
      name: 'ProgressBar',
      when: 'The number is a proportion of a whole, or a long operation moving through stages.',
    },
    {
      name: 'DataTable',
      when:
        'There are more figures than a person reads at a glance, or they need comparing against each ' +
        'other rather than noticing on their own.',
    },
    {
      name: 'StatusPill',
      when: 'What is being shown is a STATE rather than a figure — Draft, In review, Closed.',
    },
  ],

  accessibility:
    'A clickable tile becomes a real button in the tab order — role, tabindex and Enter/Space are ' +
    'handled, and the focus ring is drawn. The figure is set in tabular figures so a column of them ' +
    'lines up. Three things are left to the author. The tile is not a live region: a figure that ' +
    'changes while somebody is looking at it announces nothing. The trend arrow is decorative and ' +
    'the direction is carried by colour, so the delta string itself has to say which way it went — ' +
    '"+12%" does, "12%" does not. And the intent tint is colour alone: a figure that is bad has to ' +
    'read as bad in words too.',

  examples: [
    {
      key: 'tiles',
      label: 'A row of figures',
      summary:
        'Four tiles as they read across the head of a surface: one bare figure, and the three trend ' +
        'directions with the delta each one colours.',
      placement: [
        { component: 'caos-stat-tile', inputs: { label: 'Open orders', value: '248' } },
        { component: 'caos-stat-tile', inputs: { label: 'Closed this month', value: '31', delta: '+12%', trend: 'up' } },
        { component: 'caos-stat-tile', inputs: { label: 'Cancelled', value: '9', delta: '-3', trend: 'down' } },
        { component: 'caos-stat-tile', inputs: { label: 'Avg. days to close', value: '4.2', delta: '0.0', trend: 'flat' } },
      ],
    },
    {
      key: 'intents',
      label: 'The four tints',
      summary:
        'The whole tile tinted to say something about the FIGURE — which is a different statement ' +
        'from the trend colour above, where only the delta was coloured and the number itself was not.',
      placement: [
        { component: 'caos-stat-tile', inputs: { label: 'In process', value: '17', intent: 'info' } },
        { component: 'caos-stat-tile', inputs: { label: 'Closed this month', value: '31', intent: 'success' } },
        { component: 'caos-stat-tile', inputs: { label: 'Due this week', value: '6', intent: 'warning' } },
        { component: 'caos-stat-tile', inputs: { label: 'Overdue', value: '3', intent: 'danger' } },
      ],
    },
    {
      key: 'clickable',
      label: 'A figure you can follow',
      summary:
        'The tile as a deep link: it takes focus, warms to the accent on hover, and reports the ' +
        'press so the host can open the records behind the number.',
      placement: {
        component: 'caos-stat-tile',
        inputs: { label: 'Awaiting approval', value: '12', delta: '+4', trend: 'up', clickable: true },
      },
    },
  ],
};

export default docs;
